/* Geração de requisições e snippets (curl / Node / Python / PHP) para a API externa.
 * Só os endpoints que existem em backend/src/routes/apiRoutes.ts:
 *   POST /api/messages/send            (JSON ou multipart "medias")
 *   POST /api/messages/send/linkImage  (JSON: number, url, caption)
 *   POST /api/messages/checkNumber     (JSON: number)
 */
import { getBackendUrl } from "../../config";

/** URL pública da API que o cliente deve chamar: `REACT_APP_BACKEND_URL` na plataforma,
 *  `${origin}/backend` quando o painel é acessado por subdomínio/domínio próprio do tenant. */
export const BASE_URL = String(getBackendUrl() || "").replace(/\/+$/, "");

export const TOKEN_PLACEHOLDER = "SEU_TOKEN";

export const MODES = {
  text: { path: "/api/messages/send", kind: "json" },
  media: { path: "/api/messages/send", kind: "multipart" },
  image: { path: "/api/messages/send/linkImage", kind: "json" },
  check: { path: "/api/messages/checkNumber", kind: "json" },
};

export const LANGS = [
  { key: "curl", label: "curl" },
  { key: "node", label: "Node.js" },
  { key: "python", label: "Python" },
  { key: "php", label: "PHP" },
];

const clean = (v) => (v == null ? "" : String(v).trim());

/**
 * Monta a requisição a partir do modo e dos valores do construtor.
 * Devolve `{ url, path, kind, fields }` onde `fields` é uma lista ordenada
 * de `{ key, value, file? }` (só campos preenchidos).
 */
export function buildRequest(mode, v = {}) {
  const def = MODES[mode] || MODES.text;
  const fields = [];
  const number = clean(v.number).replace(/\D/g, "");
  fields.push({ key: "number", value: number || "5511999999999" });

  if (mode === "text" || mode === "media") {
    const body = clean(v.body);
    if (mode === "text") fields.push({ key: "body", value: body || "Olá! Esta é uma mensagem enviada pela API." });
    else if (body) fields.push({ key: "body", value: body });
    if (mode === "media") fields.push({ key: "medias", value: v.fileName || "arquivo.pdf", file: true });
    if (clean(v.userId)) fields.push({ key: "userId", value: clean(v.userId) });
    if (clean(v.queueId)) fields.push({ key: "queueId", value: clean(v.queueId) });
    if (v.sendSignature) fields.push({ key: "sendSignature", value: true });
    if (v.closeTicket) fields.push({ key: "closeTicket", value: true });
  }
  if (mode === "image") {
    fields.push({ key: "url", value: clean(v.url) || "https://exemplo.com/imagem.jpg" });
    const caption = clean(v.caption);
    if (caption) fields.push({ key: "caption", value: caption });
  }

  return { url: BASE_URL + def.path, path: def.path, kind: def.kind, fields };
}

const jsonOf = (fields) =>
  fields.reduce((acc, f) => {
    acc[f.key] = f.value;
    return acc;
  }, {});

const shQuote = (s) => `'${String(s).replace(/'/g, `'\\''`)}'`;
const dq = (s) => `"${String(s).replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
const pyLit = (v) => (typeof v === "boolean" ? (v ? "True" : "False") : dq(v));
const phpLit = (v) => (typeof v === "boolean" ? (v ? "true" : "false") : `'${String(v).replace(/\\/g, "\\\\").replace(/'/g, "\\'")}'`);
const jsLit = (v) => (typeof v === "boolean" ? String(v) : dq(v));

const indentJson = (obj, pad) => JSON.stringify(obj, null, 2).split("\n").join("\n" + pad);

function curlSnippet(req, token) {
  const lines = [`curl -X POST "${req.url}" \\`, `  -H "Authorization: Bearer ${token}" \\`];
  if (req.kind === "json") {
    lines.push(`  -H "Content-Type: application/json" \\`);
    lines.push(`  -d ${shQuote(JSON.stringify(jsonOf(req.fields), null, 2))}`);
  } else {
    req.fields.forEach((f, i) => {
      const last = i === req.fields.length - 1;
      const val = f.file ? `medias=@./${f.value}` : `${f.key}=${f.value}`;
      lines.push(`  -F ${dq(val)}${last ? "" : " \\"}`);
    });
  }
  return lines.join("\n");
}

function nodeSnippet(req, token) {
  if (req.kind === "json") {
    return [
      `// Node.js 18+ (fetch nativo)`,
      `const response = await fetch(${dq(req.url)}, {`,
      `  method: "POST",`,
      `  headers: {`,
      `    Authorization: "Bearer ${token}",`,
      `    "Content-Type": "application/json",`,
      `  },`,
      `  body: JSON.stringify(${indentJson(jsonOf(req.fields), "  ")}),`,
      `});`,
      ``,
      `console.log(response.status, await response.json());`,
    ].join("\n");
  }
  const appends = req.fields.map((f) =>
    f.file
      ? `form.append("medias", new Blob([fs.readFileSync("./${f.value}")]), "${f.value}");`
      : `form.append(${dq(f.key)}, ${jsLit(f.value)});`
  );
  return [
    `// Node.js 18+ (fetch + FormData nativos)`,
    `import fs from "node:fs";`,
    ``,
    `const form = new FormData();`,
    ...appends,
    ``,
    `const response = await fetch(${dq(req.url)}, {`,
    `  method: "POST",`,
    `  headers: { Authorization: "Bearer ${token}" },`,
    `  body: form,`,
    `});`,
    ``,
    `console.log(response.status, await response.json());`,
  ].join("\n");
}

function pythonSnippet(req, token) {
  const dict = (fields) =>
    fields.length
      ? `{\n${fields.map((f) => `    ${dq(f.key)}: ${pyLit(f.value)},`).join("\n")}\n}`
      : "{}";
  if (req.kind === "json") {
    return [
      `import requests`,
      ``,
      `url = ${dq(req.url)}`,
      `headers = {"Authorization": "Bearer ${token}"}`,
      `payload = ${dict(req.fields)}`,
      ``,
      `r = requests.post(url, headers=headers, json=payload)`,
      `print(r.status_code, r.json())`,
    ].join("\n");
  }
  const data = req.fields.filter((f) => !f.file);
  const file = req.fields.find((f) => f.file);
  return [
    `import requests`,
    ``,
    `url = ${dq(req.url)}`,
    `headers = {"Authorization": "Bearer ${token}"}`,
    `data = ${dict(data)}`,
    `files = {"medias": open("./${file ? file.value : "arquivo.pdf"}", "rb")}`,
    ``,
    `r = requests.post(url, headers=headers, data=data, files=files)`,
    `print(r.status_code, r.json())`,
  ].join("\n");
}

function phpSnippet(req, token) {
  const arr = (fields) =>
    `[\n${fields
      .map((f) => `    '${f.key}' => ${f.file ? `new CURLFile('./${f.value}')` : phpLit(f.value)},`)
      .join("\n")}\n  ]`;
  const isJson = req.kind === "json";
  return [
    `<?php`,
    `$ch = curl_init(${phpLit(req.url)});`,
    `curl_setopt_array($ch, [`,
    `  CURLOPT_POST => true,`,
    `  CURLOPT_RETURNTRANSFER => true,`,
    `  CURLOPT_HTTPHEADER => [`,
    `    'Authorization: Bearer ${token}',`,
    ...(isJson ? [`    'Content-Type: application/json',`] : []),
    `  ],`,
    `  CURLOPT_POSTFIELDS => ${isJson ? `json_encode(${arr(req.fields)})` : arr(req.fields)},`,
    `]);`,
    ``,
    `$resposta = curl_exec($ch);`,
    `$status = curl_getinfo($ch, CURLINFO_HTTP_CODE);`,
    `curl_close($ch);`,
    ``,
    `echo $status, ' ', $resposta;`,
  ].join("\n");
}

export function generateSnippets(req, token) {
  const tk = token || TOKEN_PLACEHOLDER;
  return {
    curl: curlSnippet(req, tk),
    node: nodeSnippet(req, tk),
    python: pythonSnippet(req, tk),
    php: phpSnippet(req, tk),
  };
}

export function maskToken(token) {
  const s = String(token || "");
  if (s.length <= 8) return "•".repeat(Math.max(8, s.length));
  return `${s.slice(0, 4)}${"•".repeat(Math.min(14, s.length - 8))}${s.slice(-4)}`;
}
